import { useQueryCertificateAll } from "@/api/certificate/certificate";
import { RegistrationType } from "@/api/enums/RegistrationType";
import { useAvailableExams } from "@/hooks/useAvailableExams";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/atoms/ToolTip";
import { Perimeter } from "@/lib/perimeter";
import { FormControl, FormField, FormItem, FormLabel } from "@atoms/Form";
import { RadioGroup, RadioGroupItem } from "@atoms/RadioGroup";
import { CircleHelp } from "lucide-react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

interface RegistrationTypeRadioProps {
  candidateId?: number;
}

const RegistrationTypeRadio = ({ candidateId }: RegistrationTypeRadioProps) => {
  const { control } = useFormContext();

  const { exams } = useAvailableExams({
    registrationType: RegistrationType.RENEW,
    candidateId,
  });
  const { data: certificates } = useQueryCertificateAll(candidateId);

  const canRenew = useMemo(() => exams.length > 0, [exams]);

  const canExtend = useMemo(
    () =>
      certificates?.some(
        (c) =>
          // can't extend if there is no valid certificate
          new Date(c.expirationDate) >= new Date() &&
          // can't extend full certificate
          !Perimeter.equals(Perimeter.toCode(c.perimeter), Perimeter.full())
      ) ?? false,
    [certificates]
  );

  return (
    <div>
      <h2>Type d'examen</h2>
      <FormField
        control={control}
        name="type"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <RadioGroup
                name={field.name}
                defaultValue={field.value}
                onValueChange={field.onChange}
                className="flex flex-row gap-16 w-full mt-1"
              >
                {[
                  {
                    value: RegistrationType.INITIAL,
                    msg: "Initial",
                  },

                  {
                    value: RegistrationType.RENEW,
                    msg: "Renouvellement d'un certificat",
                    disabled: !canRenew,
                  },
                  ...(canExtend
                    ? [
                        {
                          value: RegistrationType.EXTENSION,
                          msg: "Extension d'un certificat valide",
                        },
                      ]
                    : []),
                ].map(({ value, msg, disabled }) => {
                  const itemId = `${field.name}${value}`;

                  return (
                    <div className="flex items-center self-stretch" key={value}>
                      <RadioGroupItem
                        disabled={disabled}
                        id={itemId}
                        value={value}
                        className="flex items-center justify-center gap-2"
                      />
                      <FormLabel htmlFor={itemId}>{msg}</FormLabel>
                      {disabled && value === RegistrationType.RENEW && (
                        <TooltipProvider>
                          <Tooltip delayDuration={100}>
                            <TooltipTrigger
                              className="ml-2"
                              onClick={(e) => e.preventDefault()}
                            >
                              <CircleHelp className="w-4 h-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Vous ne remplissez pas les conditions pour
                              renouveler un certificat.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};

export default RegistrationTypeRadio;
